interface Tag {
  id: string
  name: string
}

interface FilterableItem {
  id: string
  isBookmarked?: boolean
  tags?: Tag[]
  updatedAt: string
  createdAt: string
}

export type FilterFunction<T> = (item: T) => boolean

export interface FilterConfig<T extends FilterableItem> {
  search?: {
    value: string
    getName: (item: T) => string
  }
  bookmark?: boolean
  tags?: string[]
  custom?: FilterFunction<T>[]
}

export type SortMode = 'Default' | 'Alphabetical' | 'Last Edited' | 'Created'
export type SortOrder = 'Ascending' | 'Descending'

export interface SortConfig<T extends FilterableItem> {
  mode: SortMode
  order: SortOrder | null
  getName: (item: T) => string
}

/**
 * Create a composable filter function from filter config
 */
export function createFilter<T extends FilterableItem>(config: FilterConfig<T>): FilterFunction<T> {
  return (item: T) => {
    // Search filter
    if (config.search?.value) {
      const name = config.search.getName(item).toLowerCase()
      const searchValue = config.search.value.toLowerCase()
      if (!name.includes(searchValue)) return false
    }

    // Bookmark filter
    if (config.bookmark === true) {
      if (item.isBookmarked !== true) return false
    }

    // Tag filters (all selected tags must be present)
    if (config.tags && config.tags.length > 0) {
      const itemTagIds = item.tags?.map((tag) => tag.id) || []
      if (!config.tags.every((tagId) => itemTagIds.includes(tagId))) {
        return false
      }
    }

    // Custom filters (all must pass)
    if (config.custom && config.custom.length > 0) {
      if (!config.custom.every((filter) => filter(item))) {
        return false
      }
    }

    return true
  }
}

/**
 * Create a composable sort function from sort config
 */
export function createSort<T extends FilterableItem>(
  config: SortConfig<T>
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    if (config.mode === 'Default' || !config.order) {
      return 0
    }

    let compareResult = 0

    if (config.mode === 'Alphabetical') {
      compareResult = config.getName(a).localeCompare(config.getName(b))
    } else if (config.mode === 'Last Edited') {
      compareResult = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    } else if (config.mode === 'Created') {
      compareResult = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    }

    if (config.order === 'Ascending') {
      return config.mode === 'Alphabetical' ? compareResult : -compareResult
    } else {
      return config.mode === 'Alphabetical' ? -compareResult : compareResult
    }
  }
}

/**
 * Apply filters and sorting to a list of items
 */
export function applyFiltersAndSort<T extends FilterableItem>(
  items: T[],
  filterConfig: FilterConfig<T>,
  sortConfig: SortConfig<T>
): T[] {
  const filter = createFilter(filterConfig)
  const sort = createSort(sortConfig)

  return items.filter(filter).sort(sort)
}
